import type { SupabaseClient } from '@supabase/supabase-js';
import { resolveClient } from './supabase';
import type { Node } from '@lingaai/types';

/**
 * GraphQuery - A pragmatic query builder that wraps Supabase queries
 *
 * This is a thin wrapper around Supabase's existing query builder, NOT a new query engine.
 * It provides a fluent API for constructing queries while leveraging Supabase's type-safe,
 * tested functionality.
 *
 * Benefits:
 * - Uses Supabase's existing query builder (type-safe, tested)
 * - Leverages existing indexes
 * - No dynamic SQL generation
 * - Debuggable
 * - Works with RLS
 * - Client-side composition
 *
 * @example
 * ```typescript
 * const results = await GraphQuery
 *   .from('Organization')
 *   .where('industry', 'SaaS')
 *   .whereNested('geo.state', 'CA')
 *   .search('sequoia')
 *   .limit(25)
 *   .execute();
 * ```
 */
export class GraphQuery {
  private supabase: SupabaseClient;
  private query: any;
  private tenantIdValue?: string;

  private constructor(supabase: SupabaseClient, nodeType: string, selectFields?: string) {
    this.supabase = supabase;
    this.query = this.supabase
      .from('nodes')
      .select(selectFields || '*')
      .eq('type', nodeType)
      .is('deleted_at', null);
  }

  /**
   * Start building a query for a specific node type
   *
   * @param nodeType - The type of node to query (e.g., 'Organization', 'Person', 'Deal')
   * @param client - Optional Supabase client (defaults to global client)
   * @returns A new GraphQuery builder instance
   *
   * @example
   * ```typescript
   * const query = GraphQuery.from('Organization');
   * ```
   */
  static from(nodeType: string, client?: SupabaseClient): GraphQuery {
    const supabase = resolveClient(client);
    return new GraphQuery(supabase, nodeType);
  }

  /**
   * Start building a query with minimal field selection for performance
   *
   * @param nodeType - The type of node to query
   * @param client - Optional Supabase client
   * @returns A new GraphQuery builder instance with minimal fields
   *
   * @example
   * ```typescript
   * const query = GraphQuery.minimal('Organization');
   * // Fetches only: id, type, props->name, props->geo, props->status, props->industry, props->organization_type
   * ```
   */
  static minimal(nodeType: string, client?: SupabaseClient): GraphQuery {
    const supabase = resolveClient(client);
    const selectFields = 'id, type, props->name, props->geo, props->status, props->industry, props->organization_type';
    return new GraphQuery(supabase, nodeType, selectFields);
  }

  /**
   * Filter by a top-level property field
   *
   * @param field - The property field name (e.g., 'industry', 'status')
   * @param value - The value or array of values to match
   * @returns The query builder for chaining
   *
   * @example
   * ```typescript
   * query.where('industry', 'SaaS')
   * query.where('status', ['active', 'pending'])
   * ```
   */
  where(field: string, value: any): this {
    if (Array.isArray(value)) {
      this.query = this.query.in(`props->>${field}`, value);
    } else {
      this.query = this.query.eq(`props->>${field}`, value);
    }
    return this;
  }

  /**
   * Filter by a nested JSONB property path
   *
   * @param path - Dot-separated path (e.g., 'geo.city', 'geo.state')
   * @param value - The value to match
   * @returns The query builder for chaining
   *
   * @example
   * ```typescript
   * query.whereNested('geo.city', 'San Francisco')
   * query.whereNested('geo.state', 'CA')
   * ```
   */
  whereNested(path: string, value: any): this {
    const parts = path.split('.');
    let jsonbPath = 'props';

    for (let i = 0; i < parts.length - 1; i++) {
      jsonbPath += `->'${parts[i]}'`;
    }
    jsonbPath += `->>'${parts[parts.length - 1]}'`;

    if (Array.isArray(value)) {
      this.query = this.query.in(jsonbPath, value);
    } else {
      this.query = this.query.eq(jsonbPath, value);
    }
    return this;
  }

  /**
   * Filter by a numeric range
   *
   * @param field - The property field name
   * @param operator - Comparison operator ('>', '>=', '<', '<=')
   * @param value - The numeric value to compare against
   * @returns The query builder for chaining
   *
   * @example
   * ```typescript
   * query.whereRange('amount', '>', 1000000)
   * query.whereRange('employee_count', '>=', 50)
   * ```
   */
  whereRange(field: string, operator: '>' | '>=' | '<' | '<=', value: number): this {
    const jsonbPath = `props->>${field}`;

    switch (operator) {
      case '>':
        this.query = this.query.gt(jsonbPath, value);
        break;
      case '>=':
        this.query = this.query.gte(jsonbPath, value);
        break;
      case '<':
        this.query = this.query.lt(jsonbPath, value);
        break;
      case '<=':
        this.query = this.query.lte(jsonbPath, value);
        break;
    }
    return this;
  }

  /**
   * Add full-text search using the search_vector column
   *
   * @param term - The search term (uses PostgreSQL websearch_to_tsquery)
   * @returns The query builder for chaining
   *
   * @example
   * ```typescript
   * query.search('sequoia capital')
   * ```
   */
  search(term: string): this {
    this.query = this.query.textSearch('search_vector', term);
    return this;
  }

  /**
   * Filter by tenant ID (for multi-tenant isolation)
   *
   * @param tenantId - The tenant UUID
   * @returns The query builder for chaining
   *
   * @example
   * ```typescript
   * query.tenant(tenantId)
   * ```
   */
  tenant(tenantId: string): this {
    this.tenantIdValue = tenantId;
    this.query = this.query.eq('tenant_id', tenantId);
    return this;
  }

  /**
   * Limit the number of results
   *
   * @param count - Maximum number of results to return
   * @returns The query builder for chaining
   *
   * @example
   * ```typescript
   * query.limit(25)
   * ```
   */
  limit(count: number): this {
    this.query = this.query.limit(count);
    return this;
  }

  /**
   * Order results by a field
   *
   * @param field - The field to order by (use 'created_at', 'updated_at', or props fields)
   * @param direction - Sort direction ('asc' or 'desc')
   * @returns The query builder for chaining
   *
   * @example
   * ```typescript
   * query.orderBy('created_at', 'desc')
   * query.orderBy('props->name', 'asc')
   * ```
   */
  orderBy(field: string, direction: 'asc' | 'desc' = 'asc'): this {
    this.query = this.query.order(field, { ascending: direction === 'asc' });
    return this;
  }

  /**
   * Execute the query and return results
   *
   * @returns Promise resolving to array of nodes
   * @throws Error if query fails
   *
   * @example
   * ```typescript
   * const results = await query.execute();
   * ```
   */
  async execute(): Promise<Node[]> {
    const { data, error } = await this.query;

    if (error) {
      throw new Error(`GraphQuery failed: ${error.message}`);
    }

    return data as Node[];
  }

  /**
   * Execute the query and return a single result
   *
   * @returns Promise resolving to a single node or null
   * @throws Error if query fails
   *
   * @example
   * ```typescript
   * const result = await query.single();
   * ```
   */
  async single(): Promise<Node | null> {
    this.query = this.query.limit(1).single();
    const { data, error } = await this.query;

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw new Error(`GraphQuery failed: ${error.message}`);
    }

    return data as Node | null;
  }

  /**
   * Execute the query and return count only
   *
   * @returns Promise resolving to the count of matching nodes
   * @throws Error if query fails
   *
   * @example
   * ```typescript
   * const count = await query.count();
   * ```
   */
  async count(): Promise<number> {
    const { count, error } = await this.query.select('*', { count: 'exact', head: true });

    if (error) {
      throw new Error(`GraphQuery count failed: ${error.message}`);
    }

    return count || 0;
  }

  /**
   * Find shortest path between the current node(s) and a target node
   *
   * @param targetNodeId - UUID of the target node
   * @param options - Path finding options
   * @returns Promise resolving to path information
   *
   * @example
   * ```typescript
   * const path = await GraphQuery
   *   .from('Person')
   *   .where('email', 'nicholas@sterling.com')
   *   .findPath(carolineId, {
   *     edgeTypes: ['REPORTS_TO'],
   *     bidirectional: true,
   *     maxDepth: 10
   *   });
   * ```
   */
  async findPath(
    targetNodeId: string,
    options?: {
      edgeTypes?: string[];
      bidirectional?: boolean;
      maxDepth?: number;
    }
  ): Promise<{
    found: boolean;
    path?: Array<{ id: string; type: string; name: string }>;
    hops?: number;
  }> {
    if (!this.tenantIdValue) {
      throw new Error('tenant() must be called before findPath()');
    }

    const startNodes = await this.execute();
    if (startNodes.length === 0) {
      return { found: false };
    }

    const startNodeId = startNodes[0]!.id;
    const edgeTypes = options?.edgeTypes || [];
    const bidirectional = options?.bidirectional ?? true;
    const maxDepth = options?.maxDepth ?? 10;

    if (!bidirectional) {
      throw new Error('Unidirectional path finding not yet implemented. Use bidirectional: true');
    }

    const { data, error } = await this.supabase.rpc('find_path_bidirectional', {
      p_tenant_id: this.tenantIdValue,
      p_start_node_id: startNodeId,
      p_target_node_id: targetNodeId,
      p_edge_types: edgeTypes.length > 0 ? edgeTypes : null,
      p_max_depth: maxDepth,
    });

    if (error) {
      throw new Error(`findPath failed: ${error.message}`);
    }

    if (!data || data.length === 0) {
      return { found: false };
    }

    return {
      found: true,
      path: data[0].path,
      hops: data[0].hops,
    };
  }

  /**
   * Traverse graph from current node(s) following specified edge types
   *
   * @param edgeType - Type of edge to follow (e.g., 'REPORTS_TO', 'WORKS_AT')
   * @param options - Traversal options
   * @returns Promise resolving to array of connected nodes
   *
   * @example
   * ```typescript
   * // Find all reports under Amanda
   * const reports = await GraphQuery
   *   .from('Person')
   *   .where('email', 'amanda@sterling.com')
   *   .traverse('REPORTS_TO', {
   *     direction: 'incoming',
   *     maxDepth: 10
   *   });
   * ```
   */
  async traverse(
    edgeType: string,
    options?: {
      direction?: 'outgoing' | 'incoming' | 'both';
      maxDepth?: number;
      filters?: Record<string, any>;
    }
  ): Promise<Node[]> {
    if (!this.tenantIdValue) {
      throw new Error('tenant() must be called before traverse()');
    }

    const startNodes = await this.execute();
    if (startNodes.length === 0) {
      return [];
    }

    const startNodeId = startNodes[0]!.id;
    const direction = options?.direction ?? 'outgoing';
    const maxDepth = options?.maxDepth ?? 5;

    const { data, error } = await this.supabase.rpc('traverse_graph', {
      p_tenant_id: this.tenantIdValue,
      p_start_node_id: startNodeId,
      p_edge_type: edgeType,
      p_direction: direction,
      p_max_depth: maxDepth,
    });

    if (error) {
      throw new Error(`traverse failed: ${error.message}`);
    }

    let results = (data as Node[]) || [];

    if (options?.filters) {
      results = results.filter((node) => {
        for (const [key, value] of Object.entries(options.filters!)) {
          const nodeValue = (node.props as any)[key];
          if (Array.isArray(value)) {
            if (!value.includes(nodeValue)) return false;
          } else {
            if (nodeValue !== value) return false;
          }
        }
        return true;
      });
    }

    return results;
  }

  /**
   * Get all nodes connected to current node(s) via specified edge types
   *
   * @param edgeTypes - Array of edge types to follow
   * @param maxDepth - Maximum traversal depth (default: 3)
   * @returns Promise resolving to array of connected nodes
   *
   * @example
   * ```typescript
   * // Find all people connected to Nicholas via REPORTS_TO
   * const connected = await GraphQuery
   *   .from('Person')
   *   .where('email', 'nicholas@sterling.com')
   *   .connected(['REPORTS_TO'], 5);
   * ```
   */
  async connected(edgeTypes: string[], maxDepth: number = 3): Promise<Node[]> {
    if (!this.tenantIdValue) {
      throw new Error('tenant() must be called before connected()');
    }

    const startNodes = await this.execute();
    if (startNodes.length === 0) {
      return [];
    }

    const startNodeId = startNodes[0]!.id;

    const { data, error } = await this.supabase.rpc('get_connected_nodes', {
      p_tenant_id: this.tenantIdValue,
      p_start_node_id: startNodeId,
      p_edge_types: edgeTypes,
      p_max_depth: maxDepth,
    });

    if (error) {
      throw new Error(`connected failed: ${error.message}`);
    }

    return (data as Node[]) || [];
  }
}

/**
 * Helper functions for calling specific search RPC functions
 */

/**
 * Search organizations by industry
 *
 * @param tenantId - Tenant UUID
 * @param industries - Array of industry names to filter by
 * @param searchTerm - Optional search term for FTS
 * @param limit - Maximum results (default 25)
 * @param client - Optional Supabase client
 * @returns Promise resolving to array of organization nodes
 *
 * @example
 * ```typescript
 * const orgs = await searchOrganizationsByIndustry(
 *   tenantId,
 *   ['SaaS', 'Fintech'],
 *   'sequoia',
 *   25
 * );
 * ```
 */
export async function searchOrganizationsByIndustry(tenantId: string, industries: string[], searchTerm?: string, limit: number = 25, client?: SupabaseClient): Promise<Node[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.rpc('search_organizations_by_industry', {
    p_tenant_id: tenantId,
    p_industries: industries,
    p_search_term: searchTerm || null,
    p_limit: limit,
  });

  if (error) {
    throw new Error(`searchOrganizationsByIndustry failed: ${error.message}`);
  }

  return data as Node[];
}

/**
 * Search deals by criteria (stage and amount range)
 *
 * @param tenantId - Tenant UUID
 * @param stages - Optional array of stages to filter by
 * @param minAmount - Optional minimum amount
 * @param maxAmount - Optional maximum amount
 * @param searchTerm - Optional search term for FTS
 * @param limit - Maximum results (default 25)
 * @param client - Optional Supabase client
 * @returns Promise resolving to array of deal nodes
 *
 * @example
 * ```typescript
 * const deals = await searchDealsByCriteria(
 *   tenantId,
 *   ['Series A', 'Series B'],
 *   5000000,
 *   null,
 *   'AI startup',
 *   25
 * );
 * ```
 */
export async function searchDealsByCriteria(
  tenantId: string,
  stages?: string[],
  minAmount?: number,
  maxAmount?: number,
  searchTerm?: string,
  limit: number = 25,
  client?: SupabaseClient
): Promise<Node[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.rpc('search_deals_by_criteria', {
    p_tenant_id: tenantId,
    p_stages: stages || null,
    p_min_amount: minAmount || null,
    p_max_amount: maxAmount || null,
    p_search_term: searchTerm || null,
    p_limit: limit,
  });

  if (error) {
    throw new Error(`searchDealsByCriteria failed: ${error.message}`);
  }

  return data as Node[];
}

/**
 * Find people by organization
 *
 * @param tenantId - Tenant UUID
 * @param organizationId - Organization node UUID
 * @param searchTerm - Optional search term for FTS
 * @param limit - Maximum results (default 25)
 * @param client - Optional Supabase client
 * @returns Promise resolving to array of person nodes with organization info
 *
 * @example
 * ```typescript
 * const people = await findPeopleByOrganization(
 *   tenantId,
 *   orgId,
 *   'john',
 *   25
 * );
 * ```
 */
export async function findPeopleByOrganization(tenantId: string, organizationId: string, searchTerm?: string, limit: number = 25, client?: SupabaseClient): Promise<Node[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.rpc('find_people_by_organization', {
    p_tenant_id: tenantId,
    p_organization_id: organizationId,
    p_search_term: searchTerm || null,
    p_limit: limit,
  });

  if (error) {
    throw new Error(`findPeopleByOrganization failed: ${error.message}`);
  }

  return data as Node[];
}

/**
 * Search nodes by status
 *
 * @param tenantId - Tenant UUID
 * @param nodeType - Node type to search
 * @param statuses - Array of status values to filter by
 * @param searchTerm - Optional search term for FTS
 * @param limit - Maximum results (default 25)
 * @param client - Optional Supabase client
 * @returns Promise resolving to array of nodes
 *
 * @example
 * ```typescript
 * const tasks = await searchNodesByStatus(
 *   tenantId,
 *   'Task',
 *   ['active', 'pending'],
 *   null,
 *   25
 * );
 * ```
 */
export async function searchNodesByStatus(tenantId: string, nodeType: string, statuses: string[], searchTerm?: string, limit: number = 25, client?: SupabaseClient): Promise<Node[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.rpc('search_nodes_by_status', {
    p_tenant_id: tenantId,
    p_node_type: nodeType,
    p_statuses: statuses,
    p_search_term: searchTerm || null,
    p_limit: limit,
  });

  if (error) {
    throw new Error(`searchNodesByStatus failed: ${error.message}`);
  }

  return data as Node[];
}

/**
 * Search organizations by location using RPC function
 *
 * @param tenantId - Tenant UUID
 * @param city - Optional city name
 * @param state - Optional state name or abbreviation
 * @param country - Optional country name
 * @param searchTerm - Optional search term for FTS
 * @param limit - Maximum results (default 25)
 * @param client - Optional Supabase client
 * @returns Promise resolving to array of organization nodes
 *
 * @example
 * ```typescript
 * const orgs = await searchOrganizationsByLocation(
 *   tenantId,
 *   'San Francisco',
 *   'CA',
 *   null,
 *   null,
 *   25
 * );
 * ```
 */
export async function searchOrganizationsByLocation(
  tenantId: string,
  city?: string,
  state?: string,
  country?: string,
  searchTerm?: string,
  limit: number = 25,
  client?: SupabaseClient
): Promise<Node[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.rpc('find_organizations_by_location', {
    p_tenant_id: tenantId,
    p_city: city || null,
    p_state: state || null,
    p_country: country || null,
    p_search_term: searchTerm || null,
    p_limit: limit,
  });

  if (error) {
    throw new Error(`searchOrganizationsByLocation failed: ${error.message}`);
  }

  return data as Node[];
}
